import {
  QualityDetailForm,
  QualityDetailTaskItem,
  QualityDocMeta,
  QualityImprovementItem,
  QualityObjectiveItem,
  QualityPlanForm,
  QualityReportForm,
  QualityReportResultItem,
} from '../types/qualityPolicy';

function uid(prefix: string) {
  return `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
}

export function emptyMeta(): QualityDocMeta {
  return {
    docNo: '',
    preparedBy: '',
    reviewedBy: '',
    approvedBy: '',
    preparedDate: '',
    reviewedDate: '',
    approvedDate: '',
  };
}

export function emptyObjective(): QualityObjectiveItem {
  return {
    id: uid('obj'),
    code: '',
    title: '',
    target: '',
    measure: '',
    owner: '',
    dueDate: '',
  };
}

export function emptyTask(): QualityDetailTaskItem {
  return {
    id: uid('task'),
    objectiveCode: '',
    taskName: '',
    method: '',
    owner: '',
    startDate: '',
    endDate: '',
    kpi: '',
    targetValue: '',
  };
}

export function emptyResult(): QualityReportResultItem {
  return {
    id: uid('res'),
    objectiveCode: '',
    objectiveTitle: '',
    target: '',
    actual: '',
    achievementRate: '',
    resultStatus: '',
    cause: '',
    action: '',
  };
}

export function emptyImprovement(): QualityImprovementItem {
  return {
    id: uid('imp'),
    item: '',
    owner: '',
    dueDate: '',
    status: '',
  };
}

export function defaultPlanForm(year: number): QualityPlanForm {
  return {
    schemaVersion: 1,
    kind: 'PLAN',
    meta: emptyMeta(),
    managementPolicy: '',
    companyMotto: '',
    environmentAnalysis: '',
    policyStatement: '',
    scope: '당사 전 사업장 및 품질경영시스템 적용 범위',
    organization: '',
    objectives: [emptyObjective()],
    annualSchedule: '',
    remarks: '',
  };
}

export function defaultDetailForm(year: number): QualityDetailForm {
  return {
    schemaVersion: 1,
    kind: 'DETAIL',
    meta: emptyMeta(),
    tasks: [emptyTask()],
    resourceNotes: '',
    remarks: '',
  };
}

export function defaultReportForm(year: number): QualityReportForm {
  return {
    schemaVersion: 1,
    kind: 'REPORT',
    meta: emptyMeta(),
    periodFrom: `${year}-01-01`,
    periodTo: `${year}-12-31`,
    results: [emptyResult()],
    improvements: [emptyImprovement()],
    managementReview: '',
    nextYearPlan: '',
    remarks: '',
  };
}

function asObject(raw: unknown): Record<string, unknown> | null {
  return raw && typeof raw === 'object' && !Array.isArray(raw)
    ? (raw as Record<string, unknown>)
    : null;
}

function parseMeta(raw: unknown): QualityDocMeta {
  const o = asObject(raw);
  const base = emptyMeta();
  if (!o) return base;
  return {
    docNo: String(o.docNo ?? ''),
    preparedBy: String(o.preparedBy ?? ''),
    reviewedBy: String(o.reviewedBy ?? ''),
    approvedBy: String(o.approvedBy ?? ''),
    preparedDate: String(o.preparedDate ?? ''),
    reviewedDate: String(o.reviewedDate ?? ''),
    approvedDate: String(o.approvedDate ?? ''),
  };
}

/** 구버전 자유텍스트도 비고로 흡수 */
function legacyRemarks(content: string | null | undefined): string {
  const text = (content || '').trim();
  if (!text) return '';
  if (text.startsWith('{')) return '';
  return text;
}

export function parsePlanForm(content: string | null | undefined, year: number): QualityPlanForm {
  const base = defaultPlanForm(year);
  const legacy = legacyRemarks(content);
  if (!content?.trim().startsWith('{')) {
    return { ...base, remarks: legacy };
  }
  try {
    const parsed = JSON.parse(content) as Record<string, unknown>;
    if (parsed.kind && parsed.kind !== 'PLAN') {
      return { ...base, remarks: legacy || content };
    }
    const objectivesRaw = Array.isArray(parsed.objectives) ? parsed.objectives : [];
    const objectives = objectivesRaw.map((item) => {
      const o = asObject(item) || {};
      return {
        id: String(o.id || uid('obj')),
        code: String(o.code ?? ''),
        title: String(o.title ?? ''),
        target: String(o.target ?? ''),
        measure: String(o.measure ?? ''),
        owner: String(o.owner ?? ''),
        dueDate: String(o.dueDate ?? ''),
      };
    });
    return {
      schemaVersion: 1,
      kind: 'PLAN',
      meta: parseMeta(parsed.meta),
      managementPolicy: String(parsed.managementPolicy ?? ''),
      companyMotto: String(parsed.companyMotto ?? ''),
      environmentAnalysis: String(parsed.environmentAnalysis ?? ''),
      policyStatement: String(parsed.policyStatement ?? ''),
      scope: String(parsed.scope ?? base.scope),
      organization: String(parsed.organization ?? ''),
      objectives: objectives.length ? objectives : [emptyObjective()],
      annualSchedule: String(parsed.annualSchedule ?? ''),
      remarks: String(parsed.remarks ?? ''),
    };
  } catch {
    return { ...base, remarks: content || '' };
  }
}

export function parseDetailForm(content: string | null | undefined, year: number): QualityDetailForm {
  const base = defaultDetailForm(year);
  const legacy = legacyRemarks(content);
  if (!content?.trim().startsWith('{')) {
    return { ...base, remarks: legacy };
  }
  try {
    const parsed = JSON.parse(content) as Record<string, unknown>;
    if (parsed.kind && parsed.kind !== 'DETAIL') {
      return { ...base, remarks: legacy || content };
    }
    const tasksRaw = Array.isArray(parsed.tasks) ? parsed.tasks : [];
    const tasks = tasksRaw.map((item) => {
      const o = asObject(item) || {};
      return {
        id: String(o.id || uid('task')),
        objectiveCode: String(o.objectiveCode ?? ''),
        taskName: String(o.taskName ?? ''),
        method: String(o.method ?? ''),
        owner: String(o.owner ?? ''),
        startDate: String(o.startDate ?? ''),
        endDate: String(o.endDate ?? ''),
        kpi: String(o.kpi ?? ''),
        targetValue: String(o.targetValue ?? ''),
      };
    });
    return {
      schemaVersion: 1,
      kind: 'DETAIL',
      meta: parseMeta(parsed.meta),
      tasks: tasks.length ? tasks : [emptyTask()],
      resourceNotes: String(parsed.resourceNotes ?? ''),
      remarks: String(parsed.remarks ?? ''),
    };
  } catch {
    return { ...base, remarks: content || '' };
  }
}

export function parseReportForm(content: string | null | undefined, year: number): QualityReportForm {
  const base = defaultReportForm(year);
  const legacy = legacyRemarks(content);
  if (!content?.trim().startsWith('{')) {
    return { ...base, remarks: legacy };
  }
  try {
    const parsed = JSON.parse(content) as Record<string, unknown>;
    if (parsed.kind && parsed.kind !== 'REPORT') {
      return { ...base, remarks: legacy || content };
    }
    const resultsRaw = Array.isArray(parsed.results) ? parsed.results : [];
    const results: QualityReportResultItem[] = resultsRaw.map((item) => {
      const o = asObject(item) || {};
      const status = String(o.resultStatus ?? '');
      const resultStatus: QualityReportResultItem['resultStatus'] =
        status === 'ACHIEVED' || status === 'PARTIAL' || status === 'FAILED' ? status : '';
      return {
        id: String(o.id || uid('res')),
        objectiveCode: String(o.objectiveCode ?? ''),
        objectiveTitle: String(o.objectiveTitle ?? ''),
        target: String(o.target ?? ''),
        actual: String(o.actual ?? ''),
        achievementRate: String(o.achievementRate ?? ''),
        resultStatus,
        cause: String(o.cause ?? ''),
        action: String(o.action ?? ''),
      };
    });
    const improvementsRaw = Array.isArray(parsed.improvements) ? parsed.improvements : [];
    const improvements = improvementsRaw.map((item) => {
      const o = asObject(item) || {};
      return {
        id: String(o.id || uid('imp')),
        item: String(o.item ?? ''),
        owner: String(o.owner ?? ''),
        dueDate: String(o.dueDate ?? ''),
        status: String(o.status ?? ''),
      };
    });
    return {
      schemaVersion: 1,
      kind: 'REPORT',
      meta: parseMeta(parsed.meta),
      periodFrom: String(parsed.periodFrom ?? base.periodFrom),
      periodTo: String(parsed.periodTo ?? base.periodTo),
      results: results.length ? results : [emptyResult()],
      improvements: improvements.length ? improvements : [emptyImprovement()],
      managementReview: String(parsed.managementReview ?? ''),
      nextYearPlan: String(parsed.nextYearPlan ?? ''),
      remarks: String(parsed.remarks ?? ''),
    };
  } catch {
    return { ...base, remarks: content || '' };
  }
}

/** 추진계획 목표 → 세부과제 / 보고서 실적 초안 동기화 */
export function syncTasksFromObjectives(
  current: QualityDetailForm,
  objectives: QualityObjectiveItem[]
): QualityDetailForm {
  const filled = objectives.filter((o) => o.code.trim() || o.title.trim());
  if (!filled.length) return current;
  const existingByCode = new Map(
    current.tasks.filter((t) => t.objectiveCode.trim()).map((t) => [t.objectiveCode.trim(), t])
  );
  const tasks = filled.map((obj) => {
    const code = obj.code.trim() || obj.title.trim();
    const prev = existingByCode.get(code);
    if (prev) return prev;
    return {
      ...emptyTask(),
      objectiveCode: code,
      taskName: obj.title,
      targetValue: obj.target,
      kpi: obj.measure,
      owner: obj.owner,
      endDate: obj.dueDate,
    };
  });
  return { ...current, tasks };
}

export function syncResultsFromObjectives(
  current: QualityReportForm,
  objectives: QualityObjectiveItem[]
): QualityReportForm {
  const filled = objectives.filter((o) => o.code.trim() || o.title.trim());
  if (!filled.length) return current;
  const existingByCode = new Map(
    current.results
      .filter((r) => r.objectiveCode.trim())
      .map((r) => [r.objectiveCode.trim(), r])
  );
  const results = filled.map((obj) => {
    const code = obj.code.trim() || obj.title.trim();
    const prev = existingByCode.get(code);
    if (prev) {
      return {
        ...prev,
        objectiveTitle: prev.objectiveTitle || obj.title,
        target: prev.target || obj.target,
      };
    }
    return {
      ...emptyResult(),
      objectiveCode: code,
      objectiveTitle: obj.title,
      target: obj.target,
    };
  });
  return { ...current, results };
}
